import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { format } from 'date-fns';
import { accountingService } from '../services/accountingService';
import { Expense, AdvancePettyCash } from '../types/accounting';
import { useAuth } from '../hooks/UserContext';
import { userService } from '../services/userService';
import { User } from '../types/user';
import AddPettyCashModal from '../components/AddPettyCashModal';

const PettyCashDetailPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const [user, setUser] = useState<User | null>(null);
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [advances, setAdvances] = useState<AdvancePettyCash[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { user: currentUser } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchData = useCallback(async () => {
        if (!userId) return;
        try {
            setLoading(true);
            const numericUserId = parseInt(userId, 10);
            const [userData, userExpenses, userAdvances] = await Promise.all([
                userService.getUser(numericUserId),
                accountingService.getExpenses({ submitted_by: userId }),
                accountingService.getAdvancePettyCashForUser(numericUserId),
            ]);
            setUser(userData);
            setExpenses(userExpenses);
            setAdvances(userAdvances);
            setError(null);
        } catch (err) {
            setError('Failed to fetch petty cash details.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSave = () => {
        fetchData();
    };

    const totalReceived = advances.reduce((acc, advance) => acc + parseFloat(advance.amount), 0);
    const totalExpense = expenses.reduce((acc, expense) => acc + parseFloat(expense.amount), 0);
    
    const totalPaid = expenses
        .filter(exp => exp.status?.toLowerCase() === 'paid')
        .reduce((sum, exp) => sum + parseFloat(exp.amount), 0);

    const totalPayable = expenses
        .filter(exp => exp.status?.toLowerCase() === 'payable')
        .reduce((sum, exp) => sum + parseFloat(exp.amount), 0);

    const balance = totalReceived - totalPaid;
    const totalReceivable = Math.max(0, totalPayable - balance);


    if (currentUser?.role !== 'OWNER' && currentUser?.role !== 'MANAGER') {
        return <Typography>You are not authorized to view this page.</Typography>;
    }

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">Petty Cash Details for {user?.first_name} {user?.last_name}</Typography>
                <Button variant="contained" onClick={() => setIsModalOpen(true)}>Add Petty Cash</Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <AddPettyCashModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSave}
                paidToUser={user}
            />

            {/* Summary Section */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                {/* Left Card */}
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Account Summary</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1" color="text.secondary">Total Expense:</Typography>
                      <Typography variant="body1" color="primary.main">${totalExpense.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1" color="text.secondary">Total Received:</Typography>
                      <Typography variant="body1" color="info.main">${totalReceived.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" color="text.secondary">My Balance:</Typography>
                      <Typography variant="body1" color={balance >= 0 ? 'success.main' : 'error.main'}>
                        ${balance.toFixed(2)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
        
                {/* Right Card */}
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Payment Summary</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1" color="text.secondary">Total Paid:</Typography>
                      <Typography variant="body1" color="success.main">${totalPaid.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1" color="text.secondary">Total Payable:</Typography>
                      <Typography variant="body1" color="warning.main">${totalPayable.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" color="text.secondary">Total Receivable:</Typography>
                      <Typography variant="body1" color="error.main">${totalReceivable.toFixed(2)}</Typography>
                    </Box>
                  </CardContent>
                </Card>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Expenses</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Project</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {expenses.map(expense => (
                                    <TableRow key={expense.id}>
                                        <TableCell>{format(new Date(expense.date), 'MMM dd, yyyy')}</TableCell>
                                        <TableCell>{expense.project_details?.name || 'N/A'}</TableCell>
                                        <TableCell>{expense.expense_type}</TableCell>
                                        <TableCell>{expense.status}</TableCell>
                                        <TableCell align="right">${parseFloat(expense.amount).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Petty Cash Received</Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Paid By</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {advances.map(advance => (
                                    <TableRow key={advance.id}>
                                        <TableCell>{format(new Date(advance.date), 'MMM dd, yyyy')}</TableCell>
                                        <TableCell>{advance.paid_by_details?.first_name} {advance.paid_by_details?.last_name}</TableCell>
                                        <TableCell align="right">${parseFloat(advance.amount).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </Box>
    );
};
export default PettyCashDetailPage;
